// Flexible clock-time entry (#271 Manual Event Creation).
//
// The manual event form used a native <input type="time">, whose rigid character
// mask "blocks" the cursor: you cannot freely type, backspace or edit, and the
// segment behaviour differs per browser/locale. These helpers back a plain TEXT
// field instead, so typing is completely unconstrained, plus a dedicated AM/PM
// toggle beside it.
//
// The parsing contract (the card's "Intelligent Parsing"):
//   • 1–2 digits            → an hour ("9", "22")
//   • 3–4 digits            → hour + minutes ("930" → 9:30, "2230" → 22:30)
//   • any of : . h -        → separator ("9:30", "9.30", "9h30")
//   • a trailing am/pm/a/p  → an EXPLICIT meridiem ("7pm", "7:15 p.m.")
//   • 24-hour input is understood: an hour of 0 or ≥13 DETERMINES the meridiem
//     on its own, so typing "22" yields 10:00 with the toggle flipped to PM —
//     exactly the card's example. That case is reported as `forced: true`.
//   • an hour of 1–12 with no explicit suffix is AMBIGUOUS, so it resolves
//     against whatever the toggle currently says (`forced: false`).
//
// Everything here is pure so the rules are unit-testable without a DOM
// (see timeInput.test.js) and identical wherever a time is typed.

export const AM = 'AM';
export const PM = 'PM';

/** 12-hour clock hour (1–12) + meridiem → 24-hour hour. */
export const to24Hour = (hour12, meridiem) => {
    const h = Number(hour12);
    if (!Number.isFinite(h)) return null;
    const mer = meridiem === PM ? PM : AM;
    if (h === 12) return mer === PM ? 12 : 0;
    return mer === PM ? h + 12 : h;
};

/** 24-hour hour → the meridiem it belongs to. */
export const meridiemOf = (hour24) => (Number(hour24) >= 12 ? PM : AM);

/** 24-hour hour + minute → the "10:30" text shown in the field (12-hour clock). */
export const formatClock12 = (hour24, minute = 0) => {
    const h = ((Number(hour24) % 12) + 12) % 12 || 12;
    const m = String(Math.max(0, Number(minute) || 0)).padStart(2, '0');
    return `${h}:${m}`;
};

/**
 * Parse whatever the user typed into a concrete time.
 *
 * @param {string} raw            the field's literal text
 * @param {string} currentMeridiem the toggle's current value, used ONLY to
 *                                 resolve an ambiguous 1–12 hour
 * @returns {null | {hour24:number, minute:number, meridiem:string, forced:boolean}}
 *          null when the text is empty or not a time at all.
 */
export const parseTimeInput = (raw, currentMeridiem = AM) => {
    if (raw == null) return null;
    let s = String(raw).trim().toLowerCase().replace(/\s+/g, '');
    if (!s) return null;

    // Trailing meridiem — "pm", "p.m.", "p".
    let explicit = null;
    const suffix = s.match(/(a|p)\.?m?\.?$/);
    if (suffix) {
        explicit = suffix[1] === 'p' ? PM : AM;
        s = s.slice(0, suffix.index);
    }
    if (!s) return null;

    // Normalise every accepted separator to ':'.
    s = s.replace(/[.\-h]/g, ':');

    let hourPart;
    let minutePart;
    if (s.includes(':')) {
        const bits = s.split(':');
        if (bits.length !== 2) return null;
        const [a, b] = bits;
        if (!/^\d{1,2}$/.test(a)) return null;
        if (b !== '' && !/^\d{1,2}$/.test(b)) return null;
        hourPart = a;
        minutePart = b;
    } else if (/^\d{1,2}$/.test(s)) {
        hourPart = s;
        minutePart = '';
    } else if (/^\d{3,4}$/.test(s)) {
        hourPart = s.slice(0, s.length - 2);
        minutePart = s.slice(-2);
    } else {
        return null;
    }

    let hour = parseInt(hourPart, 10);
    const minute = minutePart === '' ? 0 : parseInt(minutePart, 10);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (minute > 59) return null;
    if (hour > 24) return null;
    if (hour === 24) hour = 0;              // "24:00" is midnight

    if (explicit) {
        // An explicit meridiem only makes sense on a 12-hour clock hour.
        if (hour > 12) return null;
        const h12 = hour === 0 ? 12 : hour;
        return { hour24: to24Hour(h12, explicit), minute, meridiem: explicit, forced: true };
    }
    if (hour === 0 || hour >= 13) {
        // 24-hour entry — the value itself settles the meridiem.
        return { hour24: hour, minute, meridiem: meridiemOf(hour), forced: true };
    }
    const mer = currentMeridiem === PM ? PM : AM;
    return { hour24: to24Hour(hour, mer), minute, meridiem: mer, forced: false };
};

/**
 * Blur/commit normalisation: turn the raw text into the canonical "10:30" form
 * plus the meridiem it implies. Returns null when the text isn't a time (the
 * caller keeps what the user typed and surfaces an error on save).
 *
 * Normalising on BLUR rather than on every keystroke is deliberate: rewriting
 * mid-typing would fight the user (rewriting "22" to "10:00" the instant it is
 * typed makes "2230" impossible). The toggle still flips live, so the "smart"
 * behaviour is visible immediately and the committed field reads 10:00 PM.
 */
export const normalizeTimeText = (raw, currentMeridiem = AM) => {
    const parsed = parseTimeInput(raw, currentMeridiem);
    if (!parsed) return null;
    return {
        text: formatClock12(parsed.hour24, parsed.minute),
        meridiem: meridiemOf(parsed.hour24),
        hour24: parsed.hour24,
        minute: parsed.minute,
    };
};
